/**
 * A mission change, written for a human.
 *
 * The confirmation modal used to print whatever came out of `JSON.stringify`,
 * so an address change read
 * `{"_id":"63f5805477b70fb3b785b1de","street":"Route des Religieuses",…}`
 * — including the fields nobody changed and the ones nobody should see.
 *
 * Each field is rendered from what it means, not from what it stores.
 */

const EMPTY = 'Aucun';

const asText = (value) => {
  if (value === null || value === undefined || value === '') return EMPTY;
  return String(value);
};

const formatAddress = (address) => {
  if (!address || typeof address !== 'object') return asText(address);
  const line = [address.street, address.postcode, address.city]
    .filter(Boolean)
    .join(', ');
  return line || EMPTY;
};

/** The motif carries its own justification, and sometimes a person's name. */
const formatReason = (reason) => {
  if (!reason || typeof reason !== 'object') return asText(reason);

  const person = [reason.proof_firstname, reason.proof_lastname]
    .filter(Boolean)
    .join(' ');
  const detail = [reason.proof, person].filter(Boolean).join(' · ');

  const label = reason.reason || reason.type;
  if (!label) return detail || EMPTY;
  return detail ? `${label} — ${detail}` : label;
};

const formatList = (value) => {
  if (!Array.isArray(value)) return asText(value);
  return value.length ? value.join(', ') : EMPTY;
};

const formatSalary = (value) => {
  const amount = typeof value === 'object' ? value?.wage_per_hour : value;
  if (amount === null || amount === undefined || amount === '') return EMPTY;
  const parsed = parseFloat(String(amount).replace(',', '.'));
  if (!Number.isFinite(parsed)) return asText(amount);
  return `${parsed.toFixed(2).replace('.', ',')} €/h`;
};

const formatContact = (contact) => {
  if (!contact || typeof contact !== 'object') return asText(contact);
  return asText(contact.name);
};

/** A shape nobody planned for: readable keys rather than a JSON dump. */
const formatUnknownObject = (value) => {
  const hidden = new Set(['_id', '__v', 'createdAt', 'updatedAt', 'id']);
  const pairs = Object.entries(value)
    .filter(([key, entry]) => !hidden.has(key) && entry !== null && entry !== '')
    .map(([key, entry]) =>
      typeof entry === 'object' ? `${key} : …` : `${key} : ${entry}`
    );
  return pairs.length ? pairs.join(' · ') : EMPTY;
};

const FORMATTERS = {
  address: formatAddress,
  reason: formatReason,
  licences: formatList,
  languages: formatList,
  tools: formatList,
  salary: formatSalary,
  contact: formatContact,
};

export const formatMissionChangeValue = (field, value) => {
  const formatter = FORMATTERS[field];
  if (formatter) return formatter(value);
  if (Array.isArray(value)) return formatList(value);
  if (value && typeof value === 'object') return formatUnknownObject(value);
  return asText(value);
};

export const MISSION_CHANGE_EMPTY_LABEL = EMPTY;
